import { select, Store } from '@ngrx/store';
import { inject, Injectable } from '@angular/core';
import { filter, map, tap } from 'rxjs/operators';
import { UserApiService } from './api/user-api.service';
import {
  USER_ROLE,
  UserInfo,
  verifiedUserRoles,
} from '../store/reducers/session-info.reducer';
import {
  dataPrivacyConfigFetched,
  userInfoFetched,
} from '../store/actions/session-info.actions';
import { AccountApiService } from './api/account-api.service';
import { AccountTerminationChanged } from '../store/actions/user-account.actions';
import { BehaviorSubject, combineLatest, forkJoin, Observable } from 'rxjs';
import {
  isAllowAccountTerminationSelector,
  userCategorySelector,
  userRoleNewSelector,
} from '../store/selectors/user-role.selectors';
import {
  selectDPO,
  selectLogoUrl,
} from '../store/selectors/app-config.selectors';
import { TranslocoService } from '@jsverse/transloco';
import {
  selectNamespacesOnboardingProvider1001,
  selectNamespacesOnboardingProvider1002,
  selectNamespacesOnboardingProvider1003,
  selectNamespacesOnboardingProvider1004,
  selectNamespacesOnboardingProvider1005,
  selectNamespacesOnboardingProvider1006,
  selectNamespacesOnboardingProvider1007,
} from '../store/selectors/api-config.selectors';
import { CurUserInfoProvider } from './cur-user-info.provider';
import { UserTypeInfo } from '@xt/domain-model';
import { userInfoIsLoaded } from '@xt/user-context';
import { NativeClientService } from '@shared/services/native-client.service';
import { userOnboardingLevelSelector } from '@core/store/selectors/user-profile.selectors';

@Injectable({
  providedIn: 'root',
})
export class UserInfoService {
  private namespaceOnboardingProvider: BehaviorSubject<any> =
    new BehaviorSubject(null);
  public namespaceOnboardingProvider$ =
    this.namespaceOnboardingProvider.asObservable();
  private curUserInfoProvider = inject(CurUserInfoProvider);
  private nativeService = inject(NativeClientService);

  public readonly userTypeInfo$: Observable<UserTypeInfo> =
    this.curUserInfoProvider.state$.pipe(
      filter(userInfoIsLoaded),
      map((x) => x.userTypeInfo),
    );

  constructor(
    private store: Store,
    private userApiService: UserApiService,
    private accountApiService: AccountApiService,
    private transloco: TranslocoService,
  ) {}

  public fetchData() {
    this.getUserInfo$('initiated from UserInfoService').subscribe((data) => {
      if (data.roles.includes(USER_ROLE.DPO)) {
        forkJoin({
          dpoData: this.store.pipe(selectDPO),
          logoUrl: this.store.pipe(selectLogoUrl),
        })
          .pipe(
            map((data) => {
              const mappedData = [];
              for (const key in data.dpoData) {
                if (data.dpoData.hasOwnProperty(key)) {
                  mappedData.push({
                    name: this.transloco.translate(
                      'app.dataPrivacyConfig.' + key + '.title',
                    ),
                    description: this.transloco.translate(
                      'app.dataPrivacyConfig.' + key + '.description',
                    ),
                    url: data.dpoData[key],
                    logo: data.logoUrl,
                  });
                }
              }
              return mappedData;
            }),
          )
          .subscribe((data) => {
            this.store.dispatch(
              dataPrivacyConfigFetched({
                dataPrivacyConfig: {
                  monitoring: data,
                  reports: data,
                },
              }),
            );
          });
      }
    });
    this.getUserConfig$().subscribe();
    this.store
      .select(isAllowAccountTerminationSelector)
      .subscribe(
        (isAllow) => isAllow && this.getAccountTerminationStatus$().subscribe(),
      );
  }

  public getUserInfo$(testParam?: string) {
    this.curUserInfoProvider.setStateLoading();
    return this.userApiService.getUserInfo().pipe(
      map((userInfo) => {
        if (this.nativeService.isNative) {
          userInfo.authentication_type = this.nativeService.authenticationType;
        }
        return userInfo;
      }),
      tap({
        next: (response) => this.curUserInfoProvider.setUserInfo(response),
        error: (err) => this.curUserInfoProvider.setStateError(err),
      }),
      tap((response: UserInfo) => {
        this.store.dispatch(userInfoFetched({ userInfo: response }));
      }),
    );
  }

  public getUserConfig$() {
    return combineLatest([
      this.store.pipe(selectNamespacesOnboardingProvider1001),
      this.store.pipe(selectNamespacesOnboardingProvider1002),
      this.store.pipe(selectNamespacesOnboardingProvider1003),
      this.store.pipe(selectNamespacesOnboardingProvider1004),
      this.store.pipe(selectNamespacesOnboardingProvider1005),
      this.store.pipe(selectNamespacesOnboardingProvider1006),
      this.store.pipe(selectNamespacesOnboardingProvider1007),
    ]).pipe(
      tap(
        ([
          namespacesOnboardingProvider1001,
          namespacesOnboardingProvider1002,
          namespacesOnboardingProvider1003,
          namespacesOnboardingProvider1004,
          namespacesOnboardingProvider1005,
          namespacesOnboardingProvider1006,
          namespacesOnboardingProvider1007,
        ]) => {
          const namespacesOnboardingProvider = [
            namespacesOnboardingProvider1001,
            namespacesOnboardingProvider1002,
            namespacesOnboardingProvider1003,
            namespacesOnboardingProvider1004,
            namespacesOnboardingProvider1005,
            namespacesOnboardingProvider1006,
            namespacesOnboardingProvider1007,
          ];
          this.namespaceOnboardingProvider.next(namespacesOnboardingProvider);
        },
      ),
    );
  }

  public getAccountTerminationStatus$() {
    return this.accountApiService.getMyAccountTerminationStatus().pipe(
      tap((status) => {
        this.store.dispatch(
          AccountTerminationChanged({ accountTerminationStatus: status }),
        );
      }),
    );
  }

  public refrehUserInfo() {
    return this.getUserInfo$('initiated from refrehUserInfo');
  }

  public readonly hasVerifiedRole$: Observable<boolean> = this.store.pipe(
    select(userRoleNewSelector),
    map(
      (roles) =>
        roles && roles.some((item) => verifiedUserRoles.includes(item)),
    ),
  );

  public readonly accessRequired$: Observable<boolean> =
    this.hasVerifiedRole$.pipe(map((hasVerifiedRole) => !hasVerifiedRole));

  public userCategory$ = this.store.pipe(select(userCategorySelector));

  public isOnboardingLevelFull$: Observable<boolean> = this.store.pipe(
    select(userOnboardingLevelSelector),
    filter((value) => !!value),
    map((level) => level === 'FULL'),
  );
}
